import { useI18N } from "@sundae/react-hooks";
import { useWalletDetails } from "@sundae/wallet-react";
import { EContractVersion, EDatumType, IComposedTx, IZapConfigArgs } from "@sundaeswap/core";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";

import type { Tx, TxComplete } from "lucid-cardano";
import { IPendingOrder, useSubmittedOrdersCache } from "../../gql/hooks/operations.query";
import { getSwapQuantitiesForOrders } from "../../pages/Exchange/hooks/useSwapTx";
import { Actions, useAppContext } from "../../stores";
import { useZapContext } from "../../stores/LiquidityContext/ZapContext/context";
import { ELiquidityFlowState, useLiquidityContext } from "../../stores/LiquidityContext/context";
import {
  EOrderTransactionBuilderState,
  OrderActions,
  useOrderContext,
} from "../../stores/OrderContext";
import { EOpTypename } from "../../types/Operation.types";
import { featureIsEnabled } from "../../utils/features.utils";
import { TransactionErrorHandler } from "../../utils/transactions/TransactionErrorHandler.class";
import { ITransactionErrorShape, useTransactionErrorHandler } from "../useTransactionErrorHandler";

export interface IUseZapArgs {
  depositArgs?: IZapConfigArgs;
}

export const useZapTx = () => {
  const {
    state: { activeZapPool, sdk },
    dispatch: appDispatch,
  } = useAppContext();
  const {
    details: { mainAddress },
  } = useWalletDetails();
  const [, setOrders] = useSubmittedOrdersCache(mainAddress);
  const {
    handlers: { setLiquidityFlowState },
  } = useLiquidityContext();
  const {
    derived: { zapAssetAmount },
  } = useZapContext();
  const {
    dispatch: orderDispatch,
    state: {
      derived: { transaction },
    },
  } = useOrderContext();

  const { t } = useI18N("transactions");
  const { handleTransactionError } = useTransactionErrorHandler(() =>
    setLiquidityFlowState(ELiquidityFlowState.error)
  );

  const config = useMemo(() => {
    if (!zapAssetAmount || !mainAddress || !activeZapPool) {
      return null;
    }

    if (!zapAssetAmount) {
      throw Error("There must be an input amount and we don't have one.");
    }

    const args: Omit<IZapConfigArgs, "zapDirection"> = {
      pool: {
        assetA: activeZapPool.assetA,
        assetB: activeZapPool.assetB,
        assetLP: activeZapPool.assetLP,
        currentFee: Number(activeZapPool.fee) / 100,
        ident: activeZapPool.ident,
        liquidity: {
          aReserve: BigInt(activeZapPool.quantityA || 0),
          bReserve: BigInt(activeZapPool.quantityB || 0),
          lpTotal: BigInt(activeZapPool.quantityLP || 0),
        },
        version: activeZapPool.version,
      },
      suppliedAsset: zapAssetAmount,
      orderAddresses: {
        DestinationAddress: {
          address: mainAddress,
          datum: {
            type: EDatumType.NONE,
          },
        },
      },
    };

    return args;
  }, [activeZapPool, zapAssetAmount, mainAddress]);

  const { mutateAsync: buildZapTx } = useMutation(
    [setLiquidityFlowState, config, sdk],
    async () => {
      if (!sdk || !config) return;

      return TransactionErrorHandler.buildTransaction(async () =>
        sdk
          .builder(
            featureIsEnabled("v3Contracts") && activeZapPool?.version === EContractVersion.V3
              ? EContractVersion.V3
              : EContractVersion.V1
          )
          .zap(config)
      );
    },
    {
      cacheTime: 0,
      onMutate() {
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.BUILD,
          orderDispatch,
          "useZapTx.buildTx.onMutate"
        );
      },
      onError({ error, transactionState }: ITransactionErrorShape) {
        OrderActions.setTransactionState(
          EOrderTransactionBuilderState.ERROR,
          orderDispatch,
          "useZapTx.buildTx.onError"
        );

        handleTransactionError(
          error,
          {
            resolutionButtonText: t("transaction.error_reset"),
            resolutionAction: () => {
              setLiquidityFlowState(ELiquidityFlowState.error);
              OrderActions.setTransaction({}, orderDispatch, "useZapTx.error.resolutionAction");
            },
          },
          transactionState,
          config as IZapConfigArgs
        );
      },
      async onSuccess(data: IComposedTx<Tx, TxComplete>) {
        if (!data) {
          return;
        }

        const completeTx = await data.build();
        OrderActions.setTransaction(
          { builtTx: data, completeTx, state: EOrderTransactionBuilderState.SUCCESS },
          orderDispatch,
          "useZapTx.buildTx.onSuccess"
        );
      },
    }
  );

  const { mutateAsync: handleZap } = useMutation(
    [setLiquidityFlowState, config],
    async () => {
      if (!config) {
        return;
      }

      // Passing `undefined` as the first argument to `handleSDKMutation` is intentional since we won't allow the user to submit without a prepared transaction.
      return sdk && TransactionErrorHandler.handleSDKMutation(undefined, transaction?.builtTx);
    },
    {
      cacheTime: 0,
      onError({ error, transactionState, cbor }: ITransactionErrorShape) {
        if (!config) return;
        handleTransactionError(
          error,
          {
            resolutionAction: () => {
              setLiquidityFlowState(ELiquidityFlowState.submittingCancelled);
            },
            resolutionButtonText: t("transaction.error_reset"),
          },
          transactionState,
          config,
          cbor
        );
      },
      onMutate() {
        setLiquidityFlowState(ELiquidityFlowState.submitting);
      },
      onSuccess(hash) {
        if (!hash) {
          return;
        }

        // set latestTxHash in exchange state
        Actions.setLatestTxHash(hash, appDispatch);

        if (!zapAssetAmount) {
          return;
        }

        const order: IPendingOrder = {
          date: new Date(),
          hash,
          opType: EOpTypename.OpSwap,
          pool: activeZapPool,
          ...getSwapQuantitiesForOrders({
            estimatedInput: zapAssetAmount.withAmount(
              Math.floor(Number(zapAssetAmount.amount / 2n))
            ),
            inputAsset: zapAssetAmount?.metadata,
            pool: activeZapPool,
          }),
        };
        setOrders((prev) => [...prev, order]);

        setLiquidityFlowState(ELiquidityFlowState.success);
        return hash;
      },
    }
  );

  return {
    buildZapTx,
    handleZap,
  };
};
